import React, { useEffect, useState, Dispatch, SetStateAction } from "react";
import { Box, Button, IconButton, Switch, Tab, Tabs } from "@mui/material";
import TableComponent from "../../components/TableComponent";
import { GridColDef, GridAlignment } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import SearchBar from "../../components/SearchBar";
import { isValidInput } from "../../utils/validations";
import moment from "moment";
import AddIcon from "@mui/icons-material/Add";
import CustomToggle from "../../helpers/CustomToggle";
import {
  useUpdateStatusMutation,
  useUpdateUserMutation,
} from "../../services/customer";
import { showToast, showError } from "../../constants/toasts";
import Chip from "../../components/Chip";
import { CHIP_COLORS } from "../../constants/chipColors";
import ApproveButton from "../../components/ApproveButton";
import { REQUEST_STATUS } from "../../utils/enums";
import RejectReasonModal from "../../Modals/rejectReason";

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface OwnerListProps {
  userData: any[];
  valuesTabs: number;
  setValueTabs: any;
  setSelectedId: Dispatch<SetStateAction<string>>;
  setOpen: Dispatch<SetStateAction<boolean>>;
  totalCount: number;
  page: any;
  handlePageChange: any;
  getUserApi: any;
  setDebouncedSearchTerm: Dispatch<SetStateAction<string>>;
  searchTerm: string;
  setSearchTerm: Dispatch<SetStateAction<string>>;
}
export function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}
const label = { inputProps: { "aria-label": "Switch demo" } };
const OwnerList: React.FC<OwnerListProps> = ({
  userData,
  valuesTabs,
  setValueTabs,
  setSelectedId,
  setOpen,
  totalCount,
  page,
  handlePageChange,
  getUserApi,
  setDebouncedSearchTerm,
  searchTerm,
  setSearchTerm,
}) => {
  const navigate = useNavigate();
  const [updateUser] = useUpdateUserMutation();
  const [userDataState, setUserDataState] = useState(userData);
  const [updatedStatus] = useUpdateStatusMutation();
  const [updateAccount] = useUpdateUserMutation();
  const [rejectReasonModal, setRejectReasonModal] = useState(false);
  const [selectedIdForReject, setSelectedIdForReject] = useState("");

  const handleStatusUpdate = async (id: string, status: boolean) => {
    const body = {
      isBlocked: status,
    };

    try {
      const response = await updatedStatus({ body, id }).unwrap();
      if (response?.statusCode === 200) {
        showToast(
          `Owner ${!status ? "activated" : "deactivated"} successfully`
        );
        getUserApi();
      } else {
        showError("Please try again");
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };
  const handleAccountStatusUpdate = async (
    id: string,
    status: string,
    rejectReasn: string = ""
  ) => {
    const body: any = {
      accountStatus: status,
      isDocumentVerified:true,
    };

    if (status !== REQUEST_STATUS.APPROVED) {
      body.rejectReasn = rejectReasn;
    }

    try {
      const response = await updateAccount({ body, id }).unwrap();
      if (response?.statusCode === 200) {
        const statusText =
          status === REQUEST_STATUS.APPROVED ? "approved" : "rejected";
        showToast(`Owner ${statusText} successfully`);
        getUserApi();
      } else {
        showError("Please try again");
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };

  const handleApprove = (id: string) => {
    handleAccountStatusUpdate(id, REQUEST_STATUS.APPROVED, "");
  };

  const handleReject = (id: string) => {
    setRejectReasonModal(true);
    setSelectedIdForReject(id);
  };

  const handleRejectSubmit = (reason: string) => {
    if (reason.trim()) {
      handleAccountStatusUpdate(
        selectedIdForReject,
        REQUEST_STATUS.REJECTED,
        reason
      );
      setRejectReasonModal(false);
    } else {
      showError("Please enter a rejection reason");
    }
  };

  React.useEffect(() => {
    setUserDataState(userData);
  }, [userData]);

  const handleChangeTabs = (event: React.SyntheticEvent, newValue: number) => {
    setValueTabs(newValue);
  };

  const columns: GridColDef<(typeof userDataState)[number]>[] = [
    {
      flex: 1,
      minWidth: 220,
      field: "firstName",
      headerName: "Name",
      editable: false,
      sortable: false,
      renderCell: (params) => {
        const { row } = params;
        return (
          <div
            className="user_block click_action"
            onClick={() => navigate(`/manage-owner/detail/${row._id}`)}
          >
            <figure>
              <img
                src={
                  row.image && row?.image !== "string"
                    ? row.image
                    : "/static/images/user_placeholder.png"
                }
                alt=""
              />
            </figure>
            <p>
              <b>{row?.firstName || row?.firstName || "-"}</b>{" "}
              <span>{row?.email}</span>
            </p>
          </div>
        );
      },
    },
    {
      flex: 1,
      minWidth: 180,
      field: "isEmailVerified",
      headerName: "Email Verified",
      editable: false,
      sortable: false,
      align: "center" as GridAlignment,
      headerAlign: "center" as GridAlignment,
      renderCell: (params: any) => {
        const { row } = params;
        return row.isEmailVerified ? (
          <Chip
            label="Verified"
            color={CHIP_COLORS.SUCCESS.color}
            backgroundColor={CHIP_COLORS.SUCCESS.backgroundColor}
          />
        ) : (
          <Chip
            label="Not Verified"
            color={CHIP_COLORS.ERROR.color}
            backgroundColor={CHIP_COLORS.ERROR.backgroundColor}
          />
        );
      },
    },
    {
      flex: 1,
      minWidth: 150,
      field: "phoneNumber",
      headerName: "Phone Number",
      editable: false,
      sortable: false,
      renderCell: (params) => <span>{params.row.phoneNumber || "-"}</span>,
    },
    {
      flex: 1,
      minWidth: 180,
      field: "isPhoneVerified",
      headerName: "Phone Verified",
      editable: false,
      sortable: false,
      align: "center" as GridAlignment,
      headerAlign: "center" as GridAlignment,
      renderCell: (params: any) => {
        const { row } = params;
        return row.isPhoneVerified ? (
          <Chip
            label="Verified"
            color={CHIP_COLORS.SUCCESS.color}
            backgroundColor={CHIP_COLORS.SUCCESS.backgroundColor}
          />
        ) : (
          <Chip
            label="Not Verified"
            color={CHIP_COLORS.ERROR.color}
            backgroundColor={CHIP_COLORS.ERROR.backgroundColor}
          />
        );
      },
    },
    ...(valuesTabs === 0
      ? [
        {
          flex: 1,
          minWidth: 180,
          field: "accountStatus",
          headerName: "Account Status",
          editable: false,
          sortable: false,
          align: "center" as GridAlignment,
          headerAlign: "center" as GridAlignment,
          renderCell: (params: any) => {
            const { row } = params;
            return (
              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  border: "1px solid #e0e0e0",
                  borderRadius: "6px",
                  padding: "8px 12px",
                  backgroundColor: "#fafafa",
                }}
              >
                <ApproveButton
                  variant="approve"
                  onClick={() => handleApprove(row._id)}
                >
                  Approve
                </ApproveButton>
                <ApproveButton
                  variant="reject"
                  onClick={() => handleReject(row._id)}
                >
                  Reject
                </ApproveButton>
              </div>
            );
          },
        },
      ]
      : []),
    {
      flex: 1,
      minWidth: 180,
      field: "createdAt",
      headerName: "Created At",
      editable: false,
      sortable: false,
      renderCell: (params: any) => (
        <span>
          {params.row.createdAt
            ? moment(params.row.createdAt).format("YYYY-MM-DD HH:mm")
            : "-"}
        </span>
      ),
    },
    {
      flex: 1,
      minWidth: 100,
      field: "status",
      headerName: "Status",
      editable: false,
      sortable: false,
      align: "center" as GridAlignment,
      headerAlign: "center" as GridAlignment,
      renderCell: (params: any) => {
        const { row } = params;
        return (
          <CustomToggle
            checked={!row?.isBlocked}
            onChange={() => handleStatusUpdate(row?._id, !row?.isBlocked)}
            id={row?._id} // size="small"
          />
        );
      },
    },
    {
      minWidth: 130,
      field: "action",
      headerName: "Action",
      editable: false,
      sortable: false,
      renderCell: (params: any) => {
        const { row } = params;
        return (
          <div className="table_actions v2">
            <Box>
              <IconButton
                onClick={() => navigate(`/manage-owner/detail/${row._id}`)}
              >
                <img src="/static/images/eye_icon.svg" alt="" />
              </IconButton>
            </Box>
            <Box>
              <IconButton
                onClick={() => navigate(`/manage-customers/edit/${row._id}`)}
              >
                <img src="/static/images/edit2_icon.svg" alt="" />
              </IconButton>
            </Box>
            <Box>
              <IconButton
                onClick={() => {
                  setOpen(true);
                  setSelectedId(row?._id);
                }}
              >
                <img src="/static/images/trash2_icon.svg" alt="" />
              </IconButton>
            </Box>
          </div>
        );
      },
    },
  ];
  let hidden = ["fullName"];
  return (
    <>
      <div className="table_header">
        <div className="left_s">
          <Box className="custom_tabs1" sx={{ width: "100%" }}>
            <Tabs
              value={valuesTabs}
              onChange={handleChangeTabs}
              aria-label="basic tabs example"
              className="custom_tabs_links"
            >
              <Tab label="Pending" {...a11yProps(0)} />
              <Tab label="Approved" {...a11yProps(1)} />
              <Tab label="Rejected" {...a11yProps(2)} />
            </Tabs>
          </Box>

        </div>
      </div>
        <div className="table_header">
          <div className="left_s">
            <Box className="search_box">
              <SearchBar
                placeholder="Search..."
                searchTerm={searchTerm}
                setDebouncedSearchTerm={setDebouncedSearchTerm}
                value={searchTerm}
                onCross={() => setSearchTerm("")}
                onChange={(val: any) => {
                  if (isValidInput(val.target.value)) {
                    setSearchTerm(val.target.value);
                  }
                }}
              />
            </Box>
          </div>

          <div className="right_s">
            <Button
              className="btn btn_primary"
              onClick={() =>
                navigate("/manage-customers/add", { state: { tab: 1 } })
              }
            >
              <AddIcon /> Add
            </Button>
          </div>
        </div>
      <div className="sorting_table">
        <TableComponent
          columns={columns?.filter((item) => !hidden.includes(item.field))}
          rows={userDataState}
          count={totalCount}
          page={page}
          onPageChange={handlePageChange}
        />
      </div>

      <RejectReasonModal
        open={rejectReasonModal}
        onClose={() => setRejectReasonModal(false)}
        setOpen={setRejectReasonModal}
        onSubmit={handleRejectSubmit}
      />
    </>
  );
};

export default OwnerList;
